// Account and User Management
//
// IMPORTANT: Invitation code gating rules
//
// Keys that accept (slug, invitationCode) must only require `slug` to return
// a non-null key. The invitationCode is included in the key array for cache
// identity (different codes = different cache entries) but must NOT gate whether
// the fetch/mutation fires.
//
// Why: the series app runs on customer domains. Investors can enter via:
//   1. /invitation/:code  → code is saved to localStorage, all keys populated
//   2. / (RootRedirect)   → no code in localStorage, slug only
//
// Path 2 is valid for 506(c)/public offerings that don't require a specific
// invitation code. The API (getAccountInfo) falls back to INVITATION_CODE_DEFAULT
// ("default") when no code is sent, and returns INV-105 for private series that
// require a specific code. The client must allow the fetch to fire so the server
// can enforce this — client-side gating on invitationCode breaks 506(c) flows.
//
// See also: api/service-1/getAccountInfo.js (server-side invitation logic)
//           pages/InvitationCode/index.js   (localStorage write on entry)
//           utils/series.js                 (getSeriesInfo reads localStorage)
//
export const SWR_ACCOUNT_INFO_KEY = (slug, invitationCode) => (slug ? [slug, invitationCode, "getAccountInfo"] : null);

export const SWR_USER_DOCUMENTS_KEY = userId => (userId ? [userId, "getUserDocuments"] : null);

export const SWR_IP_COUNTRY_KEY = userId => (userId ? [userId, "getIPCountry"] : null);

export const SWR_MESSENGER_TOKEN_KEY = portalId => (portalId ? [portalId, "getMessengerToken"] : null);

// Company and Portal Management
export const SWR_COMPANY_KEY = companyId => (companyId ? [companyId, "getCompany"] : null);

export const SWR_PORTAL_BANK_INFO_KEY = portalId => (portalId ? [portalId, "getPortalBankInfo"] : null);

export const SWR_RISK_FACTORS_KEY = portalId => (portalId ? [portalId, "getRiskFactors"] : null);

export const SWR_SERIES_PRIVATE_KEY = portalId => (portalId ? [portalId, "getSeriesPrivate"] : null);

export const SWR_FIRST_PUBLIC_SERIES_KEY = () => "getFirstPublicSeries";

export const SWR_TOKEN_PRICE_HISTORY_KEY = customerName => (customerName ? [customerName, "getTokenPriceHistory"] : null);

export const SWR_TOTAL_NON_ACCREDITED_INVESTORS_KEY = (portalId, pageName) => (portalId && pageName ? ["getTotalNonAccrediateInvestors", portalId, pageName] : null);

export const SWR_INVITATION_LINK_KEY = invitationCode => (invitationCode ? ["getInvitationLink", invitationCode] : null);

export const SWR_COMMUNITY_MEMBERS_KEY = (seriesId, type) => (seriesId && type ? ["getCommunityMembers", seriesId, type] : null);

// Portal Item (dynamic based on slug)
export const SWR_PORTAL_ITEM_KEY = slug => (slug ? [slug, "portalItem"] : null);

// S3 Asset Management
export const SWR_S3_ASSET_URL_IMAGES_REDIRECT_KEY = (token, portalId, id, key) => (token && portalId && id && key ? ["getS3AssetURLImagesRedirect", token, portalId, id, key] : null);

// Investment Management
export const SWR_SERIES_INVESTMENTS_KEY = seriesId => (seriesId ? ["getSeriesInvestments", seriesId] : null);

export const SWR_TOKEN_ISSUED_SERIES_KEY = userId => (userId ? [userId, "getTokenIssuedSeries"] : null);

export const SWR_INVESTMENT_TRANSACTIONS_KEY = investmentId => (investmentId ? ["getInvestmentTransactions", investmentId] : null);

// Wallet Management
export const SWR_WALLETS_KEY = userId => (userId ? [userId, "getWallets"] : null);

// Grant Management
export const SWR_USER_EQUITY_GRANTS_KEY = userId => (userId ? ["getUserEquityGrants", userId] : null);

// User Progress (for KYC flow)
export const SWR_USER_PROGRESS_KEY = userId => (userId ? [userId, "userProgress"] : null);

// SWR Mutation Keys (for useSWRMutation hooks)
export const SWR_CANCEL_INVESTMENT_KEY = investmentId => (investmentId ? ["cancelInvestment", investmentId] : null);

export const SWR_SIGN_INVESTMENT_KEY = seriesId => (seriesId ? ["signInvestment", seriesId] : null);

export const SWR_CREATE_INVESTMENT_KEY = seriesId => (seriesId ? ["createInvestment", seriesId] : null);

export const SWR_CREATE_SOFT_COMMITMENT_KEY = seriesId => (seriesId ? ["createSoftCommitment", seriesId] : null);

export const SWR_SIGN_CONTRACT_KEY = seriesId => (seriesId ? ["signContract", seriesId] : null);

export const SWR_START_BANK_TRANSFER_KEY = investmentId => (investmentId ? ["startBankTransfer", investmentId] : null);

export const SWR_START_ETHEREUM_TRANSFER_KEY = investmentId => (investmentId ? ["startEthereumTransfer", investmentId] : null);

export const SWR_START_SOLANA_TRANSFER_KEY = investmentId => (investmentId ? ["startSolanaTransfer", investmentId] : null);

export const SWR_START_USDC_TRANSFER_KEY = investmentId => (investmentId ? ["startUSDCTransfer", investmentId] : null);

export const SWR_START_CANTON_TRANSFER_KEY = investmentId => (investmentId ? ["startCantonTransfer", investmentId] : null);

export const SWR_ADD_WALLET_KEY = tokenSymbol => (tokenSymbol ? [tokenSymbol, "addWallet"] : null);

export const SWR_DELETE_WALLET_KEY = userId => (userId ? [userId, "deleteWallet"] : null);

export const SWR_SET_DEFAULT_WALLET_KEY = userId => (userId ? [userId, "setDefaultWallet"] : null);

export const SWR_SET_WALLET_LABEL_KEY = userId => (userId ? [userId, "setWalletLabel"] : null);

export const SWR_CREATE_GRANT_EQUITY_KEY = userId => (userId ? [{ userId }, "createGrantEquity"] : null);

export const SWR_SIGN_GRANT_EQUITY_KEY = userId => (userId ? ["signGrantEquity", userId] : null);

export const SWR_UPDATE_USER_KEY = userId => (userId ? ["updateUser", userId] : null);

export const SWR_UPDATE_COMPANY_KEY = companyId => (companyId ? ["updateCompany", companyId] : null);

export const SWR_UPDATE_COMPANY_ROLLUP_KEY = companyId => (companyId ? ["updateCompanyRollup", companyId] : null);

export const SWR_UPDATE_ACCOUNT_KEY = (slug, invitationCode) => (slug ? ["updateAccount", slug, invitationCode] : null);

export const SWR_UPDATE_SERIES_ACCOUNT_KEY = (slug, invitationCode) => (slug ? ["updateSeriesAccount", slug, invitationCode] : null);

export const SWR_UPDATE_USER_BY_SLUG_KEY = slug => (slug ? ["updateUserBySlug", slug] : null);

export const SWR_GET_USER_INFO_BY_SLUG_KEY = slug => (slug ? ["getUserInfoBySlug", slug] : null);

export const SWR_ADD_USER_DOCUMENT_KEY = userId => (userId ? [userId, "addUserDocument"] : null);

export const SWR_REQUEST_ACCRED_DOCUMENT_KEY = userId => (userId ? ["requestAccredDocument", userId] : null);

export const SWR_SEND_KYC_EMAIL_TO_BO_KEY = userId => (userId ? ["sendKYCEmailToBO", userId] : null);

export const SWR_GET_VERIFF_SESSION_KEY = userId => (userId ? [userId, "getVeriffSession"] : null);
